import * as React from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useTranslations } from '@/hooks/use-translations'
import { useCommentAutomationQuery, useSaveCommentAutomation } from '@/queries/meta'
import type { CommentChannel, InstagramCommentActivity, InstagramCommentAutomation } from '@/services/instagramService'
import { commentTextNamespace } from './commentTextNamespace'

const EMPTY_RULE: InstagramCommentAutomation = { enabled: false, keyword: '', dm_message: '', public_reply: '' }

function ActivityBadge({ entry, ns }: { entry: InstagramCommentActivity; ns: (key: string) => string }) {
  const { t } = useTranslations()
  if (entry.error) return <Badge variant="destructive">{t(ns('automation.failed'))}</Badge>
  if (entry.dm_sent) return <Badge>{t(ns('automation.dm_sent'))}</Badge>
  if (entry.skipped) return <Badge variant="outline">{t(ns('automation.skipped'))}</Badge>
  return null
}

/**
 * The auto-reply rule. Instagram's answer is the DM; a Facebook Page may
 * answer with the public reply alone, so either message is enough there.
 */
export function CommentAutomationTab({ channel }: { channel: CommentChannel }) {
  const { t } = useTranslations()
  const ns = commentTextNamespace[channel]
  const id = (field: string) => `${channel}-automation-${field}`
  const automation = useCommentAutomationQuery(channel)
  const save = useSaveCommentAutomation(channel)
  const [rule, setRule] = React.useState<InstagramCommentAutomation>(EMPTY_RULE)
  const [dmError, setDmError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (automation.data) {
      const { enabled, keyword, dm_message, public_reply } = automation.data
      setRule({ enabled, keyword, dm_message, public_reply })
    }
  }, [automation.data])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const hasAnswer = rule.dm_message.trim() || (channel === 'facebook' && rule.public_reply.trim())
    if (rule.enabled && !hasAnswer) {
      setDmError(t(ns('automation.dm_required')))
      return
    }
    setDmError(null)
    save.mutate(rule, {
      onSuccess: (saved) => {
        if (saved.feed_subscription_error) {
          toast.error(`${t(ns('automation.subscription_error'))}: ${saved.feed_subscription_error}`)
        } else {
          toast.success(t(ns('automation.saved')))
        }
      },
      onError: (err) => toast.error(`${t(ns('automation.save_error'))}: ${err.message}`),
    })
  }

  if (automation.isLoading) return <Skeleton className="h-64 w-full" />

  if (automation.error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {t(ns('load_error'))}: {automation.error.message}
      </div>
    )
  }

  const activity = automation.data?.activity ?? []

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="max-w-2xl space-y-4">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{t(ns('automation.title'))}</h2>
          <p className="text-sm text-muted-foreground">{t(ns('automation.description'))}</p>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id={id('enabled')}
            aria-label={t(ns('automation.enabled'))}
            checked={rule.enabled}
            onCheckedChange={(enabled) => setRule((current) => ({ ...current, enabled }))}
          />
          <Label htmlFor={id('enabled')}>{t(ns('automation.enabled'))}</Label>
        </div>

        <div className="space-y-1">
          <Label htmlFor={id('keyword')}>{t(ns('automation.keyword'))}</Label>
          <Input
            id={id('keyword')}
            aria-label={t(ns('automation.keyword'))}
            value={rule.keyword}
            placeholder={t(ns('automation.keyword_placeholder'))}
            onChange={(event) => setRule((current) => ({ ...current, keyword: event.target.value }))}
          />
          <p className="text-xs text-muted-foreground">{t(ns('automation.keyword_help'))}</p>
        </div>

        <div className="space-y-1">
          <Label htmlFor={id('dm')}>{t(ns('automation.dm_message'))}</Label>
          <Textarea
            id={id('dm')}
            aria-label={t(ns('automation.dm_message'))}
            rows={3}
            value={rule.dm_message}
            placeholder={t(ns('automation.dm_message_placeholder'))}
            aria-invalid={!!dmError}
            aria-describedby={dmError ? id('dm-error') : undefined}
            onChange={(event) => setRule((current) => ({ ...current, dm_message: event.target.value }))}
          />
          <FieldError id={id('dm-error')}>{dmError}</FieldError>
        </div>

        <div className="space-y-1">
          <Label htmlFor={id('public')}>{t(ns('automation.public_reply'))}</Label>
          <Input
            id={id('public')}
            aria-label={t(ns('automation.public_reply'))}
            value={rule.public_reply}
            placeholder={t(ns('automation.public_reply_placeholder'))}
            onChange={(event) => setRule((current) => ({ ...current, public_reply: event.target.value }))}
          />
        </div>

        <Button type="submit" disabled={save.isPending}>{t(ns('automation.save'))}</Button>
      </form>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">{t(ns('automation.activity_title'))}</h2>
        {activity.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t(ns('automation.no_activity'))}</p>
        ) : (
          <ul className="space-y-2">
            {activity.map((entry) => (
              <li key={`${entry.comment_id}-${entry.received_at}`} className="space-y-1 rounded-lg border p-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{channel === 'instagram' ? `@${entry.username}` : entry.username}</span>
                  <span className="text-xs text-muted-foreground">{new Date(entry.received_at).toLocaleString()}</span>
                  <ActivityBadge entry={entry} ns={ns} />
                  {entry.public_reply_sent && <Badge variant="outline">{t(ns('automation.public_reply_sent'))}</Badge>}
                </div>
                <p className="text-sm">{entry.text}</p>
                {entry.error && <p className="text-xs text-destructive">{entry.error}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
